import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Circle } from "lucide-react";
import { CHART_COLORS, PALETTE, parseDistance } from "../lib/theme";
import type { Section2 } from "../lib/types";

interface TooltipPayload {
  payload: { name: string; value: number; color: string };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
      <strong>{d.name}</strong> — {d.value} commerce{d.value > 1 ? "s" : ""}
    </div>
  );
}

interface Props {
  data: Section2;
}

export function SectionOffer({ data }: Props) {
  const pieData = data.secteurs.map((s, i) => ({
    name: s.nom,
    value: s.commerces.length,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <div>
      <div
        className="mb-4 rounded-2xl bg-white p-3.5"
        style={{ border: `1.5px solid ${PALETTE[1]}22` }}
      >
        <div
          className="mb-2.5 text-[10px] font-bold uppercase tracking-widest"
          style={{ color: PALETTE[1] }}
        >
          Répartition par secteur
        </div>
        <div className="flex items-center gap-3">
          {/* Fluid chart: 36% of the column up to 144px, square aspect.
              Percentage radii let Recharts size the rings against the
              actual container — no hard-pixel numbers, no overflow. */}
          <div
            className="aspect-square w-[36%] max-w-[144px] min-w-[112px] shrink-0"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  innerRadius="58%"
                  outerRadius="100%"
                  dataKey="value"
                  paddingAngle={2}
                >
                  {pieData.map((e, i) => (
                    <Cell key={`cell-${i}`} fill={e.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {pieData.map((d, i) => (
              <div key={`legend-${i}`} className="flex items-center gap-2">
                <span
                  className="block h-2 w-2 shrink-0 rounded-full"
                  style={{ background: d.color }}
                />
                <span className="flex-1 text-xs leading-tight text-slate-600">
                  {d.name}
                </span>
                <span
                  className="text-xs font-bold"
                  style={{ color: d.color }}
                >
                  {d.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {data.secteurs.map((s, i) => {
          const c = CHART_COLORS[i % CHART_COLORS.length];
          const sorted = [...s.commerces].sort(
            (a, b) => parseDistance(a.distance) - parseDistance(b.distance),
          );
          return (
            <div key={`sect-${i}`}>
              <div
                className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide"
                style={{ color: c }}
              >
                <Circle size={8} color={c} strokeWidth={2} fill={c} />
                {s.nom}
              </div>
              {sorted.map((cm, j) => (
                <div
                  key={`cm-${j}`}
                  className="mb-1.5 flex items-center justify-between gap-3 rounded-lg px-3 py-2.5"
                  style={{
                    background: j % 2 === 0 ? "#fafafa" : "#fff",
                    borderLeft: `3px solid ${c}`,
                  }}
                >
                  <span className="text-sm text-slate-800">{cm.nom}</span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
                    style={{ color: c, background: `${c}15` }}
                  >
                    {cm.distance}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
